import styled from 'styled-components';
import { User, Mail, Phone, Lock, Eye, EyeOff, ArrowRight, Check, AlertCircle, Building } from 'lucide-react';

// Add interfaces for props
interface ActiveProps {
  active?: boolean;
}

interface CheckedProps {
  checked?: boolean;
}

// Layout Components
export const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f9fafb;
  display: flex;
  flex-direction: column;
`;

export const MainContent = styled.main`
  flex: 1;
  max-width: 64rem;
  margin: 0 auto;
  width: 100%;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Header Components
export const Header = styled.header`
  background-color: #6d28d9;
  color: white;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const LogoIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background-color: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

export const LogoText = styled.h1`
  font-weight: bold;
`;

export const PortalTag = styled.div`
  font-size: 0.75rem;
  background-color: #5b21b6;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

// Card Components
export const Card = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-width: 28rem;
  width: 100%;
`;

export const CardHeader = styled.div`
  margin-bottom: 1.5rem;
  text-align: center;
`;

export const CardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: #5b21b6;
`;

export const CardSubtitle = styled.p`
  color: #4b5563;
  margin-top: 0.5rem;
`;

export const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

export const FormSpace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

// Form Components
export const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
`;

export const InputGroup = styled.div`
  position: relative;
`;

export const InputIcon = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding-left: 0.75rem;
  pointer-events: none;
  color: #9ca3af;
`;

export const Input = styled.input`
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  padding-left: 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
  }
  &::placeholder {
    color: #9ca3af;
  }
`;

export const InputHint = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

export const InputToggleButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  padding-right: 0.75rem;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  &:hover {
    color: #6b7280;
  }
`;

// Button Components
export const Button = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  background-color: ${props => props.disabled ? '#d1d5db' : '#7c3aed'};
  color: ${props => props.disabled ? '#6b7280' : 'white'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  &:hover {
    background-color: ${props => props.disabled ? '#d1d5db' : '#6d28d9'};
  }
  transition: background-color 0.2s;
`;

export const LinkText = styled.p`
  margin-top: 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4b5563;
`;

export const Link = styled.a`
  color: #7c3aed;
  font-weight: 500;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

// Role Selection
export const RoleButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

export const RoleButton = styled.button<ActiveProps>`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid;
  text-align: center;
  transition: all 0.2s;
  background-color: ${props => props.active ? '#7c3aed' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  border-color: ${props => props.active ? '#7c3aed' : '#d1d5db'};
  &:hover {
    background-color: ${props => props.active ? '#7c3aed' : '#f9fafb'};
  }
`;

// Alert/Info Box
export const AlertBox = styled.div`
  padding: 1rem;
  background-color: #f5f3ff;
  border-radius: 0.5rem;
  border: 1px solid #ddd6fe;
  margin: 1rem 0 1.5rem;
`;

export const AlertBoxContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

export const AlertTitle = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #5b21b6;
`;

export const AlertText = styled.p`
  font-size: 0.875rem;
  color: #4b5563;
`;

// OTP Input
export const OtpGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

export const OtpInput = styled.input`
  width: 3rem;
  height: 3.5rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: bold;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
  }
`;

// Success Icon
export const SuccessIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  background-color: #dcfce7;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #16a34a;
`;

// Password Requirements
export const PasswordRequirements = styled.div`
  padding: 0.75rem;
  background-color: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
`;

export const RequirementTitle = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

export const RequirementList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const RequirementItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #4b5563;
`;

export const RequirementCheckmark = styled.span<CheckedProps>`
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.checked ? '#dcfce7' : '#e5e7eb'};
  color: ${props => props.checked ? '#16a34a' : '#9ca3af'};
`;

// Profile Info Card
export const ProfileInfoCard = styled.div`
  padding: 1rem;
  background-color: #f5f3ff;
  border-radius: 0.5rem;
  border: 1px solid #ddd6fe;
  margin-bottom: 1.5rem;
`;

export const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

export const ProfileAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  background-color: #ddd6fe;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #5b21b6;
`;

export const ProfileName = styled.p`
  font-weight: 500;
  color: #374151;
`;

export const ProfileDetail = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
`;

export const ProfileInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

export const ProfileInfoIcon = styled.div`
  color: #6b7280;
`;

export const ProfileInfoText = styled.p`
  font-size: 0.875rem;
  color: #4b5563;
`;

export const SchoolIdText = styled.span`
  font-family: monospace;
`;

// Footer
export const Footer = styled.footer`
  background-color: white;
  border-top: 1px solid #e5e7eb;
  padding: 1rem;
`;

export const FooterContent = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const FooterText = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
`;

export const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const FooterLink = styled.a`
  font-size: 0.75rem;
  color: #4b5563;
  text-decoration: none;
  &:hover {
    color: #7c3aed;
  }
`;

// Navigation
export const StepNavigation = styled.div`
  display: flex;
  gap: 0.5rem;
  background-color: white;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

export const StepButton = styled.button<ActiveProps>`
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: ${props => props.active ? '#7c3aed' : '#f3f4f6'};
  color: ${props => props.active ? 'white' : '#4b5563'};
  border: none;
`;

// Step Indicator Components
export const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background-color: #e5e7eb;
    z-index: 0;
  }
`;

export const Step = styled.div<ActiveProps>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

export const StepNumber = styled.div<ActiveProps>`
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  background-color: ${props => props.active ? '#7c3aed' : '#e5e7eb'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  transition: all 0.2s;
`;

export const StepLabel = styled.span<ActiveProps>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.active ? '#7c3aed' : '#6b7280'};
  transition: all 0.2s;
`;

export const StepContent = styled.div`
  margin-top: 1.5rem;
`;

export const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`; 